<template>
  <div
    class="table-hscroller"
    ref="policyDetailTableRef">
    <table
      v-tablesort="autoSort"
      id="policy-details"
      class="apptable sortable dense tower-data">
      <thead ref="tableHeader">
        <tr
          ref="headerRow"
          v-if="consolidated">
          <td
            data-testid="extra"
            class="nosort"
            v-if="hasExtraColumnSlotData && !hasExtraColumnHeader">
            {{ extraColumnTitle }}
          </td>
          <td
            class="nosort"
            v-if="hasExtraColumnHeader && hasExtraColumnSlotData">
            <slot name="extraColumnHeader" />
          </td>
          <td
            class="nosort no-program"
            v-if="showWithoutProgram && hasPoliciesWithoutProgram"></td>
          <td
            data-testid="expand"
            class="child-expand-row nosort"
            v-if="hasExpandablePolicies"></td>
          <td
            class="nosort"
            v-if="visibleFields.includes('coverageType')">
            Coverage Type
          </td>
          <td
            class="nosort"
            v-if="visibleFields.includes('accountName')">
            Account Name
          </td>
          <td
            class="nosort"
            v-if="visibleFields.includes('policy')">
            Policy
          </td>
          <td
            class="nosort"
            data-testid="ai-status-header"
            v-if="canSeeAIStatus && visibleFields.includes('aiStatus')">
            AI
          </td>
          <td
            class="nosort"
            :class="{ 'cursor-pointer select-none': consolidatedSort }"
            data-testid="sort-header-term"
            v-if="visibleFields.includes('term')"
            @click="onHeaderSort('term')">
            <span class="inline-flex items-center gap-1">
              Term
              <component
                :is="headerArrowIcon"
                v-if="isHeaderSortActive('term')"
                class="h-3.5 w-3.5 text-white" />
            </span>
          </td>
          <td
            class="nosort"
            :class="{ 'cursor-pointer select-none': consolidatedSort }"
            data-testid="sort-header-carrierGroup"
            v-if="visibleFields.includes('carrierGroup')"
            @click="onHeaderSort('carrierGroup')">
            <span class="inline-flex items-center gap-1">
              Carrier
              <component
                :is="headerArrowIcon"
                v-if="isHeaderSortActive('carrierGroup')"
                class="h-3.5 w-3.5 text-white" />
            </span>
          </td>
          <td
            class="nosort"
            :class="{ 'cursor-pointer select-none': consolidatedSort }"
            data-testid="sort-header-retentionAttachment"
            v-if="visibleFields.includes('retentionAttachment')"
            @click="onHeaderSort('retentionAttachment')">
            <span class="inline-flex items-center gap-1">
              Retention / Attachment
              <component
                :is="headerArrowIcon"
                v-if="isHeaderSortActive('retentionAttachment')"
                class="h-3.5 w-3.5 text-white" />
            </span>
          </td>
          <td
            class="nosort"
            :class="{ 'cursor-pointer select-none': consolidatedSort }"
            data-testid="sort-header-limits"
            v-if="visibleFields.includes('limits')"
            @click="onHeaderSort('limits')">
            <span class="inline-flex items-center gap-1">
              Limits
              <component
                :is="headerArrowIcon"
                v-if="isHeaderSortActive('limits')"
                class="h-3.5 w-3.5 text-white" />
            </span>
          </td>
          <td
            class="nosort text-right"
            :class="{ 'cursor-pointer select-none': consolidatedSort }"
            data-testid="sort-header-premium"
            v-if="visibleFields.includes('premium')"
            @click="onHeaderSort('premium')">
            <span class="inline-flex items-center gap-1">
              Premium
              <component
                :is="headerArrowIcon"
                v-if="isHeaderSortActive('premium')"
                class="h-3.5 w-3.5 text-white" />
            </span>
          </td>
          <td
            data-testid="actions"
            class="actions nosort"></td>
        </tr>
        <tr
          ref="headerRow"
          v-else>
          <td
            data-testid="extra"
            class="nosort"
            v-if="hasExtraColumnSlotData && !hasExtraColumnHeader">
            {{ extraColumnTitle }}
          </td>
          <td
            class="nosort"
            v-if="hasExtraColumnHeader && hasExtraColumnSlotData">
            <slot name="extraColumnHeader" />
          </td>
          <td
            class="nosort no-program"
            v-if="showWithoutProgram && hasPoliciesWithoutProgram"></td>
          <td
            data-testid="expand"
            class="child-expand-row nosort"></td>
          <td
            :class="className('year')"
            data-sortkey="year"
            class="number"
            v-if="visibleFields.includes('year')">
            Year
          </td>
          <td
            :class="className('accountName')"
            data-sortkey="accountName"
            v-if="visibleFields.includes('accountName')">
            Account Name
          </td>
          <td
            :class="className('sourceAccountName')"
            data-sortkey="sourceAccountName"
            v-if="visibleFields.includes('sourceAccountName')">
            Epic Account Name
          </td>
          <td
            :class="className('accountAttributes')"
            data-sortkey="accountAttributes"
            v-if="visibleFields.includes('accountAttributes')">
            Client Attributes
          </td>
          <td
            :class="className('childCompany')"
            data-sortkey="childCompany"
            v-if="showChildren && visibleFields.includes('childCompany')">
            Account Name
          </td>
          <td
            :class="className('parentAccountName')"
            data-sortkey="parentAccountName"
            v-if="visibleFields.includes('parentAccountName')">
            Parent Account Name
          </td>
          <td
            :class="className('accountState')"
            data-sortkey="accountState"
            v-if="visibleFields.includes('accountState')">
            Client State
          </td>
          <td
            :class="className('department')"
            data-sortkey="department"
            v-if="visibleFields.includes('department')">
            Department
          </td>
          <td
            :class="className('profitCenter')"
            data-sortkey="profitCenter"
            v-if="visibleFields.includes('profitCenter')">
            Profit Center
          </td>
          <td
            :class="className('agencyName')"
            data-sortkey="agencyName"
            v-if="visibleFields.includes('agencyName')">
            CAC Entity
          </td>
          <td
            :class="className('topLevelIndustry')"
            data-sortkey="topLevelIndustry"
            v-if="visibleFields.includes('topLevelIndustry')">
            SIC Industry
          </td>
          <td
            :class="className('industry')"
            data-sortkey="industry"
            v-if="visibleFields.includes('genericIndustry')">
            Industry
          </td>
          <td
            :class="className('ownershipType')"
            data-sortkey="ownershipType"
            v-if="visibleFields.includes('ownershipType')">
            Ownership Type
          </td>
          <td
            :class="className('policyType')"
            data-sortkey="policyType"
            v-if="visibleFields.includes('policyType')">
            Policy Type
          </td>
          <td
            :class="className('coverageType')"
            data-sortkey="coverageType"
            v-if="visibleFields.includes('coverageType')">
            Coverage Type
          </td>
          <td
            :class="className('product')"
            data-sortkey="product"
            v-if="visibleFields.includes('product')">
            Product
          </td>
          <td
            :class="className('lineStatusCode')"
            data-sortkey="lineStatusCode"
            v-if="visibleFields.includes('lineStatusCode')">
            Policy Status
          </td>
          <td
            :class="className('description')"
            data-sortkey="description"
            v-if="visibleFields.includes('description')">
            Description
          </td>
          <td
            :class="className('policyID')"
            data-sortkey="policyID"
            v-if="visibleFields.includes('policyID')">
            Policy Number
          </td>
          <td
            :class="className('externalAgencyName')"
            data-sortkey="externalAgencyName"
            v-if="visibleFields.includes('externalAgencyName')">
            External Agency
          </td>
          <td
            :class="className('aiStatus')"
            class="nosort"
            data-testid="ai-status-header"
            v-if="canSeeAIStatus && visibleFields.includes('aiStatus')">
            AI Status
          </td>
          <td
            :class="className('carrierGroup')"
            data-sortkey="carrierGroup"
            v-if="visibleFields.includes('carrierGroup')">
            Carrier
          </td>
          <td
            :class="className('issuingCompanyName')"
            data-sortkey="issuingCompanyName"
            v-if="visibleFields.includes('issuingCompanyName')">
            Issuing Company Name
          </td>
          <td
            :class="className('underwriterName')"
            data-sortkey="underwriterName"
            v-if="visibleFields.includes('underwriterName')">
            Underwriter
          </td>
          <td
            :class="className('carrierRating')"
            data-sortkey="carrierRating"
            class="dataindex"
            v-if="visibleFields.includes('carrierRating')">
            AM Best Rating
          </td>
          <td
            :class="className('syndicate')"
            data-sortkey="syndicate"
            v-if="visibleFields.includes('syndicate')">
            Syndicate Number
          </td>
          <td
            :class="className('syndicateAgent')"
            data-sortkey="syndicateAgent"
            v-if="visibleFields.includes('syndicateAgent')">
            Lead Syndicate
          </td>
          <td
            :class="className('wholesaler')"
            data-sortkey="wholesaler"
            v-if="visibleFields.includes('wholesaler')">
            Wholesaler
          </td>
          <td
            :class="className('wholesalerGroup')"
            data-sortkey="wholesalerGroup"
            v-if="visibleFields.includes('wholesalerGroup')">
            Wholesaler Group
          </td>
          <td
            :class="className('enteredDate')"
            data-sortkey="enteredDate"
            class="date"
            v-if="visibleFields.includes('enteredDate')">
            Entered
          </td>
          <td
            :class="className('effectiveDate')"
            data-sortkey="effectiveDate"
            class="date"
            v-if="visibleFields.includes('effectiveDate')">
            Effective
          </td>
          <td
            :class="className('expirationDate')"
            data-sortkey="expirationDate"
            class="date"
            v-if="visibleFields.includes('expirationDate')">
            Expires
          </td>
          <td
            :class="className('retention')"
            data-sortkey="retention"
            class="money"
            v-if="visibleFields.includes('retention')">
            Retention
          </td>
          <td
            :class="className('attachmentPoint')"
            data-sortkey="attachmentPoint"
            class="money"
            v-if="visibleFields.includes('attachmentPoint')">
            Attachment
          </td>
          <td
            :class="className('occurrenceLimit')"
            data-sortkey="occurrenceLimit"
            class="money"
            v-if="visibleFields.includes('occurrenceLimit')">
            Occurrence Limit
          </td>
          <td
            :class="className('aggregateLimit')"
            data-sortkey="limit"
            class="money"
            v-if="visibleFields.includes('aggregateLimit')">
            Aggregate Limit
          </td>
          <td
            class="percent nosort"
            v-if="visibleFields.includes('ilf')">
            ILF
          </td>
          <td
            :class="className('sideAType')"
            data-sortkey="sideAType"
            class="text-left"
            v-if="visibleFields.includes('sideAType')">
            Side A
          </td>
          <td
            :class="className('runoff')"
            data-sortkey="runoff"
            class="text-center"
            v-if="visibleFields.includes('runoff')">
            Runoff
          </td>
          <td
            :class="className('quotaShareTotal')"
            data-sortkey="quotaShareTotal"
            class="money"
            v-if="visibleFields.includes('quotaShareTotal')">
            Total Layer Limit
          </td>
          <td
            :class="className('premium')"
            data-sortkey="premium"
            class="money"
            v-if="visibleFields.includes('premium')">
            Premium
          </td>
          <td
            :class="className('premiumAnnualized')"
            data-sortkey="premiumAnnualized"
            class="money"
            v-if="visibleFields.includes('premiumAnnualized')">
            Premium (Annualized)
          </td>
          <td
            :class="className('ppm')"
            data-sortkey="ppm"
            class="money"
            v-if="visibleFields.includes('ppm')">
            PPM
          </td>
          <td
            :class="className('annualizedPPM')"
            data-sortkey="annualizedPPM"
            class="money"
            v-if="visibleFields.includes('annualizedPPM')">
            PPM (Annualized)
          </td>
          <td
            :class="className('premiumPayableName')"
            data-sortkey="premiumPayableName"
            v-if="visibleFields.includes('premiumPayableName')">
            Premium Payable Name
          </td>
          <td
            :class="className('contactNameBillTo')"
            data-sortkey="contactNameBillTo"
            v-if="visibleFields.includes('contactNameBillTo')">
            Contact Bill Name
          </td>
          <td
            :class="className('commissionBilled')"
            data-sortkey="commissionBilled"
            class="money"
            v-if="visibleFields.includes('commissionBilled')">
            Commission Billed
          </td>
          <td
            :class="className('feesBilled')"
            data-sortkey="feesBilled"
            class="money"
            v-if="visibleFields.includes('feesBilled')">
            Fees Billed
          </td>
          <td
            :class="className('computedRevenue')"
            data-sortkey="computedRevenue"
            class="money"
            v-if="visibleFields.includes('computedRevenue')">
            Revenue
          </td>
          <td
            :class="className('rebateAmt')"
            data-sortkey="rebateAmt"
            class="money"
            v-if="visibleFields.includes('rebateAmt')">
            Rebate Amount
          </td>
          <td
            :class="className('auditAmt')"
            data-sortkey="auditAmt"
            class="money"
            v-if="visibleFields.includes('auditAmt')">
            Audit Amount
          </td>
          <td
            :class="className('billMode')"
            data-sortkey="billMode"
            v-if="visibleFields.includes('billMode')">
            Bill Mode
          </td>
          <td
            :class="className('peFlag')"
            data-sortkey="peFlag"
            v-if="visibleFields.includes('peFlag')">
            Layer
          </td>

          <!-- Surety fields / bonds only -->

          <td
            :class="className('currTermAmount')"
            data-sortkey="currTermAmount"
            v-if="visibleFields.includes('currTermAmount')">
            Current Term Amount
          </td>
          <td
            :class="className('obligeeName')"
            data-sortkey="obligeeName"
            v-if="visibleFields.includes('obligeeName')">
            Obligee Name
          </td>
          <td
            :class="className('obligeeAddress')"
            data-sortkey="obligeeAddress"
            v-if="visibleFields.includes('obligeeAddress')">
            Obligee Address
          </td>
          <td
            :class="className('principalName')"
            data-sortkey="principalName"
            v-if="visibleFields.includes('principalName')">
            Principal Name
          </td>
          <td
            :class="className('principalAddress')"
            data-sortkey="principalAddress"
            v-if="visibleFields.includes('principalAddress')">
            Principal Address
          </td>
          <td
            :class="className('underwritingName')"
            data-sortkey="underwritingName"
            v-if="visibleFields.includes('underwritingName')">
            Underwriting Name
          </td>
          <td
            :class="className('underwritingAddress')"
            data-sortkey="underwritingAddress"
            v-if="visibleFields.includes('underwritingAddress')">
            Underwriting Address
          </td>
          <td
            :class="className('cancelable')"
            data-sortkey="cancelable"
            v-if="visibleFields.includes('cancelable')">
            Cancelable
          </td>
          <td
            :class="className('netAmount')"
            data-sortkey="netAmount"
            v-if="visibleFields.includes('netAmount')">
            Net Amount
          </td>
          <td
            :class="className('accountExecutiveRole')"
            data-sortkey="accountExecutiveRole"
            v-if="visibleFields.includes('accountExecutiveRole')">
            Account Executive
          </td>
          <td
            :class="className('accountManagerRole')"
            data-sortkey="accountManagerRole"
            v-if="visibleFields.includes('accountManagerRole')">
            Account Manager
          </td>
          <td
            :class="className('secondaryAccountManagerRole')"
            data-sortkey="secondaryAccountManagerRole"
            v-if="visibleFields.includes('secondaryAccountManagerRole') && isAnAdminOrBrokerUser">
            Secondary Account Manager
          </td>
          <td
            :class="className('brokerRole')"
            data-sortkey="brokerRole"
            v-if="visibleFields.includes('brokerRole')">
            Broker
          </td>
          <td
            :class="className('claimsRole')"
            data-sortkey="claimsRole"
            v-if="visibleFields.includes('claimsRole')">
            Claims
          </td>
          <td
            :class="className('advisorRole')"
            data-sortkey="advisorRole"
            v-if="visibleFields.includes('advisorRole')">
            Advisor
          </td>

          <!-- end -->

          <td
            data-testid="actions"
            class="actions nosort"></td>
        </tr>
      </thead>
      <tbody>
        <template
          v-for="(policy, index) in policyHierarchy"
          :key="`${policy.uniqPolicy}-${index}`">
          <ContextMenuRoot>
            <ContextMenuTrigger as-child>
              <tr
                data-testid="policy-row"
                :class="{ ['external-policy']: isExternalPolicy(policy) }">
                <td v-if="hasExtraColumnSlotData">
                  <slot
                    name="extraColumn"
                    v-bind="{ policy }" />
                </td>
                <td v-if="showWithoutProgram && hasPoliciesWithoutProgram">
                  <div
                    class="tooltip tooltip-right flex"
                    data-tip="This Policy is not part of an existing Program">
                    <AlertTriangle
                      v-if="policy.inProgram === false"
                      :size="20"
                      class="text-red-500" />
                  </div>
                </td>
                <td
                  class="p-0 pl-1 text-right"
                  v-if="!consolidated || hasExpandablePolicies">
                  <AppButton
                    v-visible="policy.hasChildren"
                    class="bg-white/0 btn-xs"
                    @click="toggleExpand(index, policy)">
                    <Minus
                      v-if="policy.expanded"
                      :size="10"
                      class="stroke-primary" />
                    <Plus
                      v-else
                      :size="10"
                      class="stroke-primary" />
                  </AppButton>
                </td>
                <!-- Consolidated (program details) layout — CAC-5074 -->
                <template v-if="consolidated">
                  <td v-if="visibleFields.includes('coverageType')">
                    {{ policy.coverageType }}
                  </td>

                  <td v-if="visibleFields.includes('accountName')">
                    <router-link
                      v-if="!isAMarketUser"
                      :to="policy.accountID ? `/account/${policy.accountID}/policy-detail` : ''"
                      class="url-text"
                      :title="policy.uniqPolicy">
                      <div class="flex">
                        {{ policy.accountName }}
                        <ExternalLink
                          :size="20"
                          class="url-icon text-primary" />
                      </div>
                    </router-link>
                    <div v-else>{{ policy.accountName }}</div>
                  </td>

                  <td
                    class="layer-id"
                    v-if="visibleFields.includes('policy')">
                    <div class="flex flex-col">
                      <div>{{ policy.description }}</div>
                      <div class="group flex items-center font-mono text-xs text-gray-500">
                        {{ policy.policyID?.trim() || '--' }}
                        <div
                          v-if="(isAClientAccount && policy.hasPublishedDocument) || (!isAClientAccount && policy.hasDocument === true)"
                          class="ml-1 flex items-center">
                          <DocumentModal>
                            <template v-slot:activator="{ open }">
                              <button
                                class="btn-xs"
                                @click="open(policy.accountID, policy.uniqPolicy, policy.policyID)">
                                <FileText
                                  :size="14"
                                  class="hyperlink-icon stroke-primary" />
                              </button>
                            </template>
                          </DocumentModal>
                        </div>
                        <div
                          v-else-if="!isAMarketUser && !isAClientAccount"
                          class="ml-1 flex items-center">
                          <DocumentModal>
                            <template v-slot:activator="{ open }">
                              <button
                                class="invisible btn-xs group-hover:visible"
                                @click="open(policy.accountID, policy.uniqPolicy, policy.policyID)">
                                <FilePlus2
                                  :size="14"
                                  class="text-red-500" />
                              </button>
                            </template>
                          </DocumentModal>
                        </div>
                      </div>
                    </div>
                  </td>

                  <td
                    data-testid="ai-status-cell"
                    v-if="canSeeAIStatus && visibleFields.includes('aiStatus')">
                    <PolicyAIStatusCell
                      :status="policy.aiValidationStatus"
                      :binder-result-id="policy.aiBinderResultID"
                      :binder-count="policy.aiBinderCount ?? 0"
                      :policy-id="Number(policy.uniqPolicy)"
                      :account-id="policy.accountID"
                      @open-modal="openAIStatusModal($event, policy.accountID)" />
                  </td>

                  <td
                    class="whitespace-nowrap"
                    v-if="visibleFields.includes('term')">
                    <div class="flex items-center gap-1">
                      <Calendar
                        :size="14"
                        class="shrink-0 stroke-gray-400" />
                      <span>{{ formatTerm(policy) || '--' }}</span>
                    </div>
                  </td>

                  <td v-if="visibleFields.includes('carrierGroup')">
                    <router-link
                      v-if="!isAClientAccount"
                      :to="policy.carrierID ? `/carrier/${policy.carrierID}/policy-detail` : ''"
                      class="url-text">
                      <div class="flex">
                        {{ policy.carrierGroup }}
                        <ExternalLink
                          :size="20"
                          class="url-icon text-primary" />
                      </div>
                    </router-link>
                    <div v-else>{{ policy.carrierGroup }}</div>
                  </td>

                  <td
                    class="whitespace-nowrap"
                    v-if="visibleFields.includes('retentionAttachment')">
                    <span :class="retentionAttachment(policy).value ? '' : 'text-gray-400'">
                      {{ retentionAttachment(policy).label }}
                      {{ retentionAttachment(policy).value ?? '—' }}
                    </span>
                  </td>

                  <td
                    class="whitespace-nowrap"
                    v-if="visibleFields.includes('limits')">
                    <div class="flex flex-col">
                      <div class="flex items-center gap-1">
                        <span class="text-gray-500">Occ</span>
                        <span :class="limitValue(policy, 'occurrenceLimit') ? '' : 'text-gray-400'">
                          {{ limitValue(policy, 'occurrenceLimit') ?? '—' }}
                        </span>
                      </div>
                      <div class="flex items-center gap-1">
                        <span class="text-gray-500">Agg</span>
                        <span :class="limitValue(policy, 'aggregateLimit') ? '' : 'text-gray-400'">
                          {{ limitValue(policy, 'aggregateLimit') ?? '—' }}
                        </span>
                        <button
                          v-if="policy.hasSublimits"
                          class="btn-xs"
                          @click.stop="showSublimits(policy)">
                          <ExternalLink
                            :size="14"
                            class="hyperlink-icon stroke-primary" />
                        </button>
                      </div>
                    </div>
                  </td>

                  <td
                    class="text-right whitespace-nowrap"
                    v-if="visibleFields.includes('premium')">
                    {{ formatCurrency(policy.premium, true) }}
                  </td>
                </template>

                <template v-else>
                  <td
                    class="text-right"
                    v-if="visibleFields.includes('year')">
                    {{ policy.year }}
                  </td>

                  <!-- Account Name -->
                  <td v-if="visibleFields.includes('accountName')">
                    <router-link
                      v-if="!isAMarketUser"
                      :to="policy.accountID ? `/account/${policy.accountID}/policy-detail` : ''"
                      class="url-text"
                      :title="policy.uniqPolicy">
                      <div class="flex">
                        {{ policy.accountName
                        }}<span
                          v-if="policy.favoriteAccount"
                          class="text-primary"
                          >★</span
                        >
                        <ExternalLink
                          :size="20"
                          class="url-icon text-primary" />
                      </div>
                    </router-link>
                    <div v-else>
                      {{ policy.accountName }}
                    </div>
                  </td>

                  <!-- Epic Account Name -->
                  <td v-if="visibleFields.includes('sourceAccountName')">{{ policy.sourceAccountName ?? '--' }}</td>

                  <td v-if="visibleFields.includes('accountAttributes')">
                    {{ policy.accountAttributes }}
                  </td>
                  <td
                    class="layer-id"
                    v-if="showChildren && visibleFields.includes('childCompany')"
                    :data-testid="`${index}-child-data`">
                    <div>{{ policy.childCompany }}</div>
                  </td>
                  <td v-if="visibleFields.includes('parentAccountName')">
                    <router-link
                      :to="policy.parentAccountID ? `/parent/account/${policy.parentAccountID}/policy-detail` : ''"
                      class="url-text">
                      <div class="flex">
                        {{ policy.parentAccountName ?? '--' }}
                        <ExternalLink
                          :size="20"
                          class="url-icon text-primary" />
                      </div>
                    </router-link>
                  </td>
                  <td v-if="visibleFields.includes('accountState')">
                    {{ usStatesLookupMap.get(policy.accountState) ?? '--' }}
                  </td>
                  <td v-if="visibleFields.includes('department')">{{ policy.department }}</td>
                  <td v-if="visibleFields.includes('profitCenter')">{{ policy.profitCenter }}</td>
                  <td v-if="visibleFields.includes('agencyName')">{{ cacEntityFor(policy) }}</td>
                  <td v-if="visibleFields.includes('topLevelIndustry')">{{ policy.topLevelIndustry ?? '--' }}</td>
                  <td v-if="visibleFields.includes('sicIndustry')">{{ policy.topLevelIndustry ?? '--' }}</td>
                  <td v-if="visibleFields.includes('genericIndustry')">{{ policy.genericIndustry ?? '--' }}</td>
                  <td v-if="visibleFields.includes('ownershipType')">{{ policy.ownershipType ?? '--' }}</td>
                  <td v-if="visibleFields.includes('policyType')">{{ policy.policyType }}</td>
                  <td v-if="visibleFields.includes('coverageType')">{{ policy.coverageType }}</td>
                  <td v-if="visibleFields.includes('product')">{{ policy.product }}</td>
                  <td v-if="visibleFields.includes('lineStatusCode')">{{ policy.lineStatusCode }}</td>
                  <td
                    class="layer-id"
                    :data-testid="`${index}-layer-id`"
                    v-if="visibleFields.includes('description')">
                    <div>{{ policy.description }}</div>
                  </td>

                  <!-- Policy Number (Main Policy Number) -->
                  <td v-if="visibleFields.includes('policyID')">
                    <div class="group flex items-center">
                      {{ policy.policyID?.trim() || '--' }}
                      <div
                        v-if="(isAClientAccount && policy.hasPublishedDocument) || (!isAClientAccount && policy.hasDocument === true)"
                        class="ml-1 flex items-center">
                        <DocumentModal>
                          <template v-slot:activator="{ open }">
                            <button
                              class="btn-xs"
                              @click="open(policy.accountID, policy.uniqPolicy, policy.policyID)">
                              <FileText
                                :size="16"
                                class="hyperlink-icon stroke-primary" />
                            </button>
                          </template>
                        </DocumentModal>
                      </div>

                      <!-- add document -->
                      <div
                        v-else-if="!isAMarketUser && !isAClientAccount"
                        class="group ml-1 flex items-center">
                        <DocumentModal>
                          <template v-slot:activator="{ open }">
                            <button
                              class="invisible btn-xs group-hover:visible"
                              @click="open(policy.accountID, policy.uniqPolicy, policy.policyID)">
                              <FilePlus2
                                size="16"
                                class="text-red-500" />
                            </button>
                          </template>
                        </DocumentModal>
                      </div>
                    </div>
                  </td>
                  <td v-if="visibleFields.includes('externalAgencyName')">{{ externalAgencyFor(policy) }}</td>

                  <td
                    :class="className('aiStatus')"
                    data-testid="ai-status-cell"
                    v-if="canSeeAIStatus && visibleFields.includes('aiStatus')">
                    <PolicyAIStatusCell
                      :status="policy.aiValidationStatus"
                      :binder-result-id="policy.aiBinderResultID"
                      :binder-count="policy.aiBinderCount ?? 0"
                      :policy-id="Number(policy.uniqPolicy)"
                      :account-id="policy.accountID"
                      @open-modal="openAIStatusModal($event, policy.accountID)" />
                  </td>

                  <td v-if="visibleFields.includes('carrierGroup')">
                    <router-link
                      v-if="!isAClientAccount"
                      :to="policy.carrierID ? `/carrier/${policy.carrierID}/policy-detail` : ''"
                      class="url-text">
                      <div class="flex">
                        {{ policy.carrierGroup }}
                        <ExternalLink
                          :size="20"
                          class="url-icon text-primary" />
                      </div>
                    </router-link>
                    <div v-else>{{ policy.carrierGroup }}</div>
                  </td>
                  <td v-if="visibleFields.includes('issuingCompanyName')">{{ policy.issuingCompanyName }}</td>
                  <td v-if="visibleFields.includes('underwriterName')">{{ policy.underwriterName }}</td>
                  <td
                    v-if="visibleFields.includes('carrierRating')"
                    :data-index="policy.carrierRatingIndex">
                    <router-link
                      v-if="policy.carrierRating"
                      :to="`/carrier/${policy.carrierID}/insurer-ratings`"
                      data-testid="carrier-rating">
                      {{ policy.carrierRating }}
                    </router-link>
                    <div v-else>--</div>
                  </td>
                  <td v-if="visibleFields.includes('syndicate')">{{ policy.syndicate }}</td>
                  <td v-if="visibleFields.includes('syndicateAgent')">{{ policy.syndicateNumber }}-{{ policy.syndicateAgent }}</td>
                  <td v-if="visibleFields.includes('wholesaler')">{{ policy.wholesaler }}</td>
                  <td v-if="visibleFields.includes('wholesalerGroup')">{{ policy.wholesalerGroup }}</td>
                  <td
                    class="date"
                    v-if="visibleFields.includes('enteredDate')">
                    {{ filters.date(policy.enteredDate) }}
                  </td>
                  <td
                    class="date"
                    v-if="visibleFields.includes('effectiveDate')">
                    {{ filters.shorterdate(policy.effectiveDate) }}
                  </td>
                  <td
                    class="date"
                    v-if="visibleFields.includes('expirationDate')">
                    {{ filters.shorterdate(policy.expirationDate) }}
                  </td>
                  <td
                    class="money"
                    v-if="visibleFields.includes('retention')">
                    {{ formatCurrency(policy.retention, true) ?? '--' }}
                  </td>
                  <td
                    class="text-right"
                    v-if="visibleFields.includes('attachmentPoint')">
                    {{ formatCurrency(policy.attachmentPoint, true) ?? '--' }}
                  </td>
                  <td
                    class="text-right"
                    v-if="visibleFields.includes('occurrenceLimit')">
                    <div class="flex items-center justify-end gap-1">
                      {{ displayStatutory(policy.coverageType) ? 'Statutory' : (formatCurrency(policy.occurrenceLimit, true) ?? '--') }}
                      <button
                        v-if="policy.hasSublimits"
                        class="btn-xs"
                        @click.stop="showSublimits(policy)">
                        <ExternalLink
                          :size="14"
                          class="hyperlink-icon stroke-primary" />
                      </button>
                    </div>
                  </td>
                  <td
                    class="text-right"
                    v-if="visibleFields.includes('aggregateLimit')">
                    <div class="flex items-center justify-end gap-1">
                      {{ displayStatutory(policy.coverageType) ? 'Statutory' : (formatCurrency(policy.aggregateLimit, true) ?? '--') }}
                      <button
                        v-if="policy.hasSublimits"
                        class="btn-xs"
                        @click.stop="showSublimits(policy)">
                        <ExternalLink
                          :size="14"
                          class="hyperlink-icon stroke-primary" />
                      </button>
                    </div>
                  </td>
                  <td
                    class="text-right"
                    v-if="visibleFields.includes('ilf')">
                    {{ calculateILF(policies, index) ?? '--' }}
                  </td>
                  <td
                    class="text-left"
                    v-if="visibleFields.includes('sideAType')">
                    {{ policy.sideAType }}
                  </td>
                  <td
                    class="text-center"
                    v-if="visibleFields.includes('runoff')">
                    {{ policy.runoff ? 'Yes' : '--' }}
                  </td>
                  <td
                    class="text-right"
                    v-if="visibleFields.includes('quotaShareTotal')">
                    {{ formatCurrency(policy.quotaShareTotal, true) ?? '--' }}
                  </td>
                  <td
                    class="text-right"
                    v-if="visibleFields.includes('premium')">
                    {{ formatCurrency(policy.premium, true) }}
                  </td>
                  <td
                    class="text-right"
                    v-if="visibleFields.includes('premiumAnnualized')">
                    {{ formatCurrency(policy.premiumAnnualized, true) }}
                  </td>
                  <td
                    class="text-right"
                    v-if="visibleFields.includes('ppm')">
                    {{ formatCurrency(policy.ppm || calculatePPM(policy), true) ?? '--' }}
                  </td>
                  <td
                    class="text-right"
                    v-if="visibleFields.includes('annualizedPPM')">
                    {{ formatCurrency(policy.annualizedPPM, true) ?? '--' }}
                  </td>
                  <td v-if="visibleFields.includes('premiumPayableName')">{{ policy.premiumPayableName }}</td>
                  <td v-if="visibleFields.includes('contactNameBillTo')">{{ policy.contactNameBillTo }}</td>
                  <td
                    class="money"
                    v-if="visibleFields.includes('commissionBilled')">
                    {{ formatCurrency(policy.commissionBilled, true) ?? '--' }}
                  </td>
                  <td
                    class="money"
                    v-if="visibleFields.includes('feesBilled')">
                    {{ formatCurrency(policy.feesBilled, true) ?? '--' }}
                  </td>
                  <td
                    class="money"
                    v-if="visibleFields.includes('computedRevenue')">
                    {{ formatCurrency(policy.computedRevenue, true) ?? '--' }}
                  </td>
                  <td
                    class="money"
                    v-if="visibleFields.includes('rebateAmt')">
                    {{ formatCurrency(policy.rebateAmt, true) ?? '--' }}
                  </td>
                  <td
                    class="money"
                    v-if="visibleFields.includes('auditAmt')">
                    {{ formatCurrency(policy.auditAmt, true) ?? '--' }}
                  </td>
                  <td v-if="visibleFields.includes('billMode')">{{ policy.billMode }}</td>
                  <td v-if="visibleFields.includes('peFlag')">{{ policy.peFlag }}</td>

                  <!-- Surety fields / bonds only -->

                  <td
                    class="text-right"
                    v-if="visibleFields.includes('currTermAmount')">
                    {{ formatCurrency(policy.currentTermAmount, true) }}
                  </td>
                  <td
                    class="text-left"
                    v-if="visibleFields.includes('obligeeName')">
                    {{ policy.obligeeName }}
                  </td>
                  <td
                    class="text-left whitespace-pre-wrap"
                    v-if="visibleFields.includes('obligeeAddress')">
                    {{ policy.obligeeAddress }}
                  </td>
                  <td
                    class="text-left"
                    v-if="visibleFields.includes('principalName')">
                    {{ policy.principalName }}
                  </td>
                  <td
                    class="text-left whitespace-pre-wrap"
                    v-if="visibleFields.includes('principalAddress')">
                    {{ policy.principalAddress }}
                  </td>
                  <td
                    class="text-left"
                    v-if="visibleFields.includes('underwritingName')">
                    {{ policy.underwritingName }}
                  </td>
                  <td
                    class="text-left whitespace-pre-wrap"
                    v-if="visibleFields.includes('underwritingAddress')">
                    {{ policy.underwritingAddress }}
                  </td>
                  <td
                    class="text-center"
                    v-if="visibleFields.includes('cancelable')">
                    {{ policy.cancelable }}
                  </td>
                  <td
                    class="text-right"
                    v-if="visibleFields.includes('netAmount')">
                    {{ formatCurrency(policy.netAmount, true) }}
                  </td>
                  <td
                    class="text-left"
                    v-if="visibleFields.includes('accountExecutiveRole')">
                    {{ policy.accountExecutiveRole }}
                  </td>
                  <td
                    class="text-left"
                    v-if="visibleFields.includes('accountManagerRole')">
                    {{ policy.accountManagerRole }}
                  </td>
                  <td
                    class="text-left"
                    v-if="visibleFields.includes('secondaryAccountManagerRole') && isAnAdminOrBrokerUser">
                    {{ policy.secondaryAccountManagerRole }}
                  </td>
                  <td
                    class="text-left"
                    v-if="visibleFields.includes('brokerRole')">
                    {{ policy.brokerRole }}
                  </td>
                  <td
                    class="text-left"
                    v-if="visibleFields.includes('claimsRole')">
                    {{ policy.claimsRole }}
                  </td>
                  <td
                    class="text-left"
                    v-if="visibleFields.includes('advisorRole')">
                    {{ policy.advisorRole }}
                  </td>
                </template>

                <!-- end -->

                <!--
              The flex layout lives on an inner wrapper, not the <td>. `display: flex` on a
              cell overrides `display: table-cell`, so it stops stretching to the row height
              and the row's hover background leaves a gap beside multi-line rows.
            -->
                <td class="actions">
                  <div class="flex flex-row items-center justify-end gap-2">
                    <div
                      v-if="hasChip(policy)"
                      class="align-center flex h-6 w-fit justify-center self-center rounded-xl px-3"
                      :class="getPolicyChipColor(policy)">
                      <span class="my-auto">{{ getPolicyChipLabel(policy) }}</span>
                    </div>
                    <MenuDropdown
                      :items="getPolicyMenuItems(policy)"
                      :prepend-items="getPrependMenuItems?.(policy) ?? []"
                      left>
                      <template #action>
                        <label
                          tabindex="0"
                          data-testid="policy-row-actions"
                          class="btn btn-square btn-ghost btn-sm">
                          <EllipsisVertical :size="20" />
                        </label>
                      </template>
                    </MenuDropdown>
                  </div>
                </td>
              </tr>
            </ContextMenuTrigger>

            <ContextMenuPortal to="body">
              <ContextMenuContent
                class="max-w-80 min-w-60 gap-1 rounded-md bg-white p-2 text-sm outline-1 outline-gray-300 drop-shadow-xl"
                @interact-outside="outsideClicked = true">
                <!-- Edit Policy -->
                <ContextMenuItem
                  v-if="!isAMarketUser && !isAClientAccount"
                  class="flex h-6 max-w-60 items-center rounded-sm p-4 outline-none select-none hover:bg-gray-200"
                  @click="handleEditPolicyClick(policy)">
                  Edit Policy
                </ContextMenuItem>

                <!-- Upload Document -->
                <ContextMenuItem
                  v-if="!isAMarketUser && !isAClientAccount"
                  class="flex h-[25px] items-center rounded-sm p-4 outline-none select-none hover:bg-gray-200"
                  @click.stop="handleDocumentUploadMenuClick(Number(policy.accountID), Number(policy.uniqPolicy), policy.policyID)"
                  data-testid="attach-policy-document">
                  Upload Document
                </ContextMenuItem>

                <!-- Delete -->
                <ContextMenuItem
                  v-if="isExternalPolicy(policy) && canDeletePolicy(policy) && !isCarrierPerspective && !isWholesalerPerspective && !isAClientAccount && !isAMarketUser"
                  class="flex h-6 max-w-60 items-center rounded-sm p-4 text-agency-red outline-none select-none hover:bg-gray-200"
                  @click="openDeleteModal(policy)">
                  Delete Policy
                </ContextMenuItem>

                <ContextMenuSeparator
                  v-if="!isAMarketUser && !isAClientAccount"
                  class="my-2 h-px bg-gray-300" />

                <!-- Go to account -->
                <ContextMenuItem
                  v-if="policy.accountID && !isOnAccountPolicyDetailPage && !isOnAccountProgramDetailsPage && !isAMarketUser"
                  class="flex h-6 max-w-60 items-center rounded-sm p-4 outline-none select-none hover:bg-gray-200"
                  @click="$router.push(`/account/${policy.accountID}/policy-detail`)">
                  <span class="overflow-hidden text-nowrap overflow-ellipsis">View {{ policy.accountName }}</span>
                </ContextMenuItem>

                <ContextMenuItem
                  v-if="policy.parentAccountID && policy.accountID !== policy.parentAccountID && !isOnParentAccountPolicyDetailPage"
                  class="flex h-6 max-w-60 items-center rounded-sm p-4 outline-none select-none hover:bg-gray-200"
                  @click="$router.push(`/parent/account/${policy.parentAccountID}/policy-detail`)">
                  <span class="overflow-hidden text-nowrap overflow-ellipsis">View {{ policy.parentAccountName }}</span>
                </ContextMenuItem>

                <!-- Go to carrier -->
                <ContextMenuItem
                  v-if="policy.carrierID && !isAClientAccount && !isOnCarrierPolicyDetailPage"
                  class="flex h-6 max-w-60 items-center rounded-sm p-4 outline-none select-none hover:bg-gray-200"
                  @click="$router.push(`/carrier/${policy.carrierID}/policy-detail`)">
                  <span class="overflow-hidden text-nowrap overflow-ellipsis">View {{ policy.carrierGroup }}</span>
                </ContextMenuItem>

                <!-- View Sublimits & Subretentions -->
                <ContextMenuItem
                  :disabled="!policy.hasSublimits"
                  class="flex h-[25px] items-center rounded-sm p-4 outline-none select-none"
                  :class="{ 'hover:bg-gray-200': policy.hasSublimits, 'opacity-50': !policy.hasSublimits }"
                  @click="policy.hasSublimits && showSublimits(policy)"
                  data-testid="view-sublimits">
                  <span class="disabled">View Sublimits &amp; Subretentions</span>
                </ContextMenuItem>
              </ContextMenuContent>
            </ContextMenuPortal>
          </ContextMenuRoot>
          <ContextMenuRoot
            v-for="(childPolicy, childIndex) in policy.children || []"
            :key="`${policy.uniqPolicy}-${childPolicy.uniqLine}-${childIndex}`">
            <ContextMenuTrigger as-child>
              <tr
                v-show="policy.expanded"
                class="child"
                :class="{ ['last-child-row']: childIndex === (policy.children || []).length - 1 }">
                <!-- Selection and no-program cells are the parent row's, so the child needs placeholders to line up -->
                <td v-if="consolidated && hasExtraColumnSlotData" />
                <td v-if="consolidated && showWithoutProgram && hasPoliciesWithoutProgram" />
                <td v-if="!consolidated || hasExpandablePolicies" />

                <!-- Consolidated (program details) layout — must mirror the parent row's cells -->
                <template v-if="consolidated">
                  <td v-if="visibleFields.includes('coverageType')">{{ childPolicy.coverageType }}</td>
                  <td v-if="visibleFields.includes('accountName')">{{ childPolicy.accountName }}</td>
                  <td
                    class="layer-id"
                    v-if="visibleFields.includes('policy')">
                    <div class="flex flex-col">
                      <div>{{ childPolicy.description }}</div>
                      <div class="font-mono text-xs text-gray-500">{{ childPolicy.policyID?.trim() || '--' }}</div>
                    </div>
                  </td>
                  <td
                    data-testid="ai-status-cell"
                    v-if="canSeeAIStatus && visibleFields.includes('aiStatus')">
                    <PolicyAIStatusCell
                      :status="childPolicy.aiValidationStatus"
                      :binder-result-id="childPolicy.aiBinderResultID"
                      :binder-count="childPolicy.aiBinderCount ?? 0"
                      :policy-id="Number(childPolicy.uniqPolicy)"
                      :account-id="childPolicy.accountID"
                      @open-modal="openAIStatusModal($event, childPolicy.accountID)" />
                  </td>
                  <td
                    class="whitespace-nowrap"
                    v-if="visibleFields.includes('term')">
                    <div class="flex items-center gap-1">
                      <Calendar
                        :size="14"
                        class="shrink-0 stroke-gray-400" />
                      <span>{{ formatTerm(childPolicy) || '--' }}</span>
                    </div>
                  </td>
                  <td v-if="visibleFields.includes('carrierGroup')">{{ childPolicy.carrierGroup }}</td>
                  <td
                    class="whitespace-nowrap"
                    v-if="visibleFields.includes('retentionAttachment')">
                    <span :class="retentionAttachment(childPolicy).value ? '' : 'text-gray-400'">
                      {{ retentionAttachment(childPolicy).label }}
                      {{ retentionAttachment(childPolicy).value ?? '—' }}
                    </span>
                  </td>
                  <td
                    class="whitespace-nowrap"
                    v-if="visibleFields.includes('limits')">
                    <div class="flex flex-col">
                      <div class="flex items-center gap-1">
                        <span class="text-gray-500">Occ</span>
                        <span :class="limitValue(childPolicy, 'occurrenceLimit') ? '' : 'text-gray-400'">
                          {{ limitValue(childPolicy, 'occurrenceLimit') ?? '—' }}
                        </span>
                      </div>
                      <div class="flex items-center gap-1">
                        <span class="text-gray-500">Agg</span>
                        <span :class="limitValue(childPolicy, 'aggregateLimit') ? '' : 'text-gray-400'">
                          {{ limitValue(childPolicy, 'aggregateLimit') ?? '—' }}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td
                    class="text-right whitespace-nowrap"
                    v-if="visibleFields.includes('premium')">
                    {{ formatCurrency(childPolicy.premium, true) }}
                  </td>
                </template>

                <template v-else>
                  <td
                    class="text-right"
                    v-show="visibleFields.includes('year')">
                    {{ childPolicy.year }}
                  </td>
                  <td v-if="visibleFields.includes('accountName')">{{ childPolicy.accountName }}</td>
                  <td v-if="visibleFields.includes('sourceAccountName')">{{ childPolicy.sourceAccountName ?? '--' }}</td>
                  <td v-if="visibleFields.includes('accountAttributes')">{{ childPolicy.accountAttributes }}</td>
                  <td
                    class="layer-id"
                    v-if="showChildren && visibleFields.includes('childCompany')"
                    :data-testid="`${index}-child-data`">
                    <div>{{ childPolicy.childCompany }}</div>
                  </td>
                  <td v-if="visibleFields.includes('parentAccountName')">{{ childPolicy.parentAccountName ?? '--' }}</td>
                  <td v-if="visibleFields.includes('agencyName')">{{ cacEntityFor(childPolicy) }}</td>
                  <td v-if="visibleFields.includes('topLevelIndustry')">{{ childPolicy.topLevelIndustry ?? '--' }}</td>
                  <td v-if="visibleFields.includes('ownershipType')">{{ childPolicy.ownershipType ?? '--' }}</td>
                  <td v-if="visibleFields.includes('policyType')">{{ childPolicy.policyType }}</td>
                  <td v-if="visibleFields.includes('coverageType')">{{ childPolicy.coverageType }}</td>
                  <td v-if="visibleFields.includes('lineStatusCode')">{{ childPolicy.lineStatusCode }}</td>
                  <td
                    class="layer-id"
                    :data-testid="`${index}-layer-id`"
                    v-if="visibleFields.includes('description')">
                    <div>{{ childPolicy.description }}</div>
                  </td>
                  <td v-if="visibleFields.includes('policyID')">{{ childPolicy.policyID }}</td>
                  <td v-if="visibleFields.includes('externalAgencyName')">{{ externalAgencyFor(childPolicy) }}</td>
                  <td
                    data-testid="ai-status-cell"
                    v-if="canSeeAIStatus && visibleFields.includes('aiStatus')">
                    <PolicyAIStatusCell
                      :status="childPolicy.aiValidationStatus"
                      :binder-result-id="childPolicy.aiBinderResultID"
                      :binder-count="childPolicy.aiBinderCount ?? 0"
                      :policy-id="Number(childPolicy.uniqPolicy)"
                      :account-id="childPolicy.accountID"
                      @open-modal="openAIStatusModal($event, childPolicy.accountID)" />
                  </td>
                  <td v-if="visibleFields.includes('carrierGroup')">{{ childPolicy.carrierGroup }}</td>
                  <td v-if="visibleFields.includes('issuingCompanyName')">{{ childPolicy.issuingCompanyName }}</td>
                  <td v-if="visibleFields.includes('underwriterName')">{{ childPolicy.underwriterName }}</td>
                  <td
                    v-if="visibleFields.includes('carrierRating')"
                    :data-index="childPolicy.carrierRatingIndex">
                    <router-link
                      v-if="childPolicy.carrierRating"
                      :to="`/carrier/${childPolicy.carrierID}/insurer-ratings`"
                      data-testid="carrier-rating">
                      {{ childPolicy.carrierRating }}
                    </router-link>
                    <div v-else>--</div>
                  </td>
                  <td v-if="visibleFields.includes('syndicate')">{{ childPolicy.syndicate }}</td>
                  <td v-if="visibleFields.includes('syndicateAgent')">{{ childPolicy.syndicateNumber }}-{{ childPolicy.syndicateAgent }}</td>
                  <td v-if="visibleFields.includes('wholesaler')">{{ childPolicy.wholesaler }}</td>
                  <td v-if="visibleFields.includes('wholesalerGroup')">{{ childPolicy.wholesalerGroup }}</td>
                  <td
                    class="date"
                    v-if="visibleFields.includes('enteredDate')">
                    {{ filters.date(childPolicy.enteredDate) }}
                  </td>
                  <td
                    class="date"
                    v-if="visibleFields.includes('effectiveDate')">
                    {{ filters.date(childPolicy.effectiveDate) }}
                  </td>
                  <td
                    class="date"
                    v-if="visibleFields.includes('expirationDate')">
                    {{ filters.date(childPolicy.expirationDate) }}
                  </td>
                  <td
                    class="money"
                    v-if="visibleFields.includes('retention')">
                    {{ formatCurrency(childPolicy.retention, true) ?? '--' }}
                  </td>
                  <td
                    class="text-right"
                    v-if="visibleFields.includes('attachmentPoint')">
                    {{ formatCurrency(childPolicy.attachmentPoint, true) ?? '--' }}
                  </td>
                  <td
                    class="text-right"
                    v-if="visibleFields.includes('occurrenceLimit')">
                    {{ formatCurrency(childPolicy.occurrenceLimit, true) ?? '--' }}
                  </td>
                  <td
                    class="text-right"
                    v-if="visibleFields.includes('aggregateLimit')">
                    {{ formatCurrency(childPolicy.aggregateLimit, true) ?? '--' }}
                  </td>
                  <td
                    class="text-right"
                    v-if="visibleFields.includes('ilf')">
                    --
                  </td>
                  <td
                    class="text-left"
                    v-if="visibleFields.includes('sideAType')">
                    {{ childPolicy.sideAType }}
                  </td>
                  <td
                    class="text-center"
                    v-if="visibleFields.includes('runoff')">
                    {{ childPolicy.runoff ? 'Yes' : '--' }}
                  </td>
                  <td
                    class="text-right"
                    v-if="visibleFields.includes('quotaShareTotal')">
                    {{ formatCurrency(childPolicy.quotaShareTotal, true) ?? '--' }}
                  </td>
                  <td
                    class="text-right"
                    v-if="visibleFields.includes('premium')">
                    {{ formatCurrency(childPolicy.premium, true) }}
                  </td>
                  <td
                    class="text-right"
                    v-if="visibleFields.includes('ppm')">
                    {{ formatCurrency(calculatePPM(childPolicy), true) ?? '--' }}
                  </td>
                  <td
                    class="text-right"
                    v-if="visibleFields.includes('annualizedPPM')">
                    {{ formatCurrency(childPolicy.annualizedPPM, true) ?? '--' }}
                  </td>
                  <td v-if="visibleFields.includes('premiumPayableName')">{{ childPolicy.premiumPayableName }}</td>
                  <td v-if="visibleFields.includes('contactNameBillTo')">{{ childPolicy.contactNameBillTo }}</td>
                  <td
                    class="money"
                    v-if="visibleFields.includes('commissionBilled')">
                    --
                  </td>
                  <td
                    class="money"
                    v-if="visibleFields.includes('feesBilled')">
                    --
                  </td>
                  <td
                    class="money"
                    v-if="visibleFields.includes('computedRevenue')">
                    --
                  </td>
                  <td
                    class="money"
                    v-if="visibleFields.includes('rebateAmt')">
                    --
                  </td>
                  <td
                    class="money"
                    v-if="visibleFields.includes('auditAmt')">
                    --
                  </td>
                  <td v-if="visibleFields.includes('billMode')">{{ childPolicy.billMode }}</td>
                  <td v-if="visibleFields.includes('peFlag')">{{ childPolicy.peFlag }}</td>
                  <td
                    class="text-left"
                    v-if="visibleFields.includes('accountExecutiveRole')">
                    {{ childPolicy.accountExecutiveRole }}
                  </td>
                  <td
                    class="text-left"
                    v-if="visibleFields.includes('accountManagerRole')">
                    {{ childPolicy.accountManagerRole }}
                  </td>
                  <td
                    class="text-left"
                    v-if="visibleFields.includes('secondaryAccountManagerRole') && isAnAdminOrBrokerUser">
                    {{ childPolicy.secondaryAccountManagerRole }}
                  </td>
                  <td
                    class="text-left"
                    v-if="visibleFields.includes('brokerRole')">
                    {{ childPolicy.brokerRole }}
                  </td>
                  <td
                    class="text-left"
                    v-if="visibleFields.includes('claimsRole')">
                    {{ childPolicy.claimsRole }}
                  </td>
                  <td
                    class="text-left"
                    v-if="visibleFields.includes('advisorRole')">
                    {{ childPolicy.advisorRole }}
                  </td>
                </template>

                <td class="actions">
                  <MenuDropdown
                    v-if="!isCarrierPerspective && !isWholesalerPerspective && !isAClientAccount"
                    :items="getChildPolicyMenuItems(childPolicy, policy)"
                    bottom>
                    <template #action>
                      <label
                        tabindex="0"
                        class="btn btn-square text-center btn-ghost btn-sm"
                        data-testid="policy-actions-menu">
                        <EllipsisVertical :size="20" />
                      </label>
                    </template>
                  </MenuDropdown>
                </td>
              </tr>
            </ContextMenuTrigger>

            <ContextMenuContent
              class="max-w-40 min-w-40 gap-1 rounded-md bg-white p-2 text-sm outline-1 outline-gray-300 drop-shadow-xl"
              @interact-outside="outsideClicked = true">
              <!-- Edit Policy Line -->
              <ContextMenuItem
                v-if="!isAClientAccount"
                @click="handleEditPolicyClick(childPolicy, true, policy)"
                class="flex h-6 max-w-60 items-center rounded-sm p-4 outline-none select-none hover:bg-gray-200">
                Edit Policy Line
              </ContextMenuItem>
            </ContextMenuContent>
          </ContextMenuRoot>
        </template>
        <tr v-if="!policies || policies.length === 0">
          <td
            colspan="11"
            class="text-center">
            No policy information.
          </td>
        </tr>
      </tbody>
    </table>

    <AppModal
      :show="sublimitDialogShow"
      size="xl"
      :title="sublimitPolicy?.coverageType ? `${sublimitPolicy.coverageType} &ndash; Sublimits &amp; Subretentions` : ''">
      <template #content>
        <PolicySublimits
          :coverage-type="sublimitPolicy?.coverageType ?? ''"
          :sublimits="sublimits"
          :subretentions="subretentions"
          :can-edit="!isAClientAccount"
          @edit-in-modal="editSublimitsInPolicyModal" />
      </template>

      <template #actions>
        <div class="flex w-full justify-between gap-2">
          <button
            class="btn btn-sm"
            @click="downloadPolicySublimits">
            <Download size="14" />
            Download
          </button>

          <div
            class="btn btn-sm btn-primary"
            @click="closeSublimits">
            Close
          </div>
        </div>
      </template>
    </AppModal>

    <ConsolidatedEditPolicyModal
      v-if="!isAClientAccount && editPolicy"
      :show="showPolicyEditModal"
      :policy="editPolicy"
      :policy-source-id="editPolicySourceId"
      :initial-record-key="initialRecordKey"
      :initial-tab="initialEditTab"
      @close="showPolicyEditModal = false"
      @save="
        showPolicyEditModal = false;
        $emit('needs-update');
        emitRefreshKPIDataEvent();
      " />

    <DocumentModal ref="uploadDocumentModalRef" />

    <PolicyDeleteModal
      ref="policyDeleteModalRef"
      :policyNumber="deleteModalData.policyNumber"
      :accountId="deleteModalData.accountId"
      :policyId="deleteModalData.policyId"
      :show="showDeleteModal"
      @needs-update="$emit('needs-update')"
      @closed="closeDeleteModal" />

    <PolicyBinderAIStatusModal
      v-if="canSeeAIStatus"
      v-model="aiStatusModalOpen"
      :policy-id="aiStatusModalPolicyId"
      :account-id="aiStatusModalAccountId" />
  </div>
</template>

<script>
import { formatCurrency, compactMoney } from '@/utilities/Money';
import { calculateILF, calculatePPM } from '@/utilities/tower/Calculations';
import { policyService } from '@/services/api';
import PolicyDeleteModal from '@/components/PolicyDeleteModal.vue';
import {
  PolicySourceControlling,
  PolicySourceMultiCarrierSchedules,
  PolicySourceParticipant,
} from '@/utilities/PolicySources';
import PolicySublimits from '@/views/policy/PolicySublimits';
import { DownloadTablesToCSV } from '@/utilities/Download';
import { trackPolicyDetailsSort } from '@/utilities/Tracking';
import { policyDetailsType } from '@/types/PolicyDetailsType';
import { ACCEPTED_FILE_DOCUMENT_TYPES } from '@/types/AcceptedTypes';
import { IsMarketUser, IsUserABroker, IsUserAClient, IsUserAnAdmin } from '@/utilities/AuthUtils';
import Store from '@/store';
import { Events } from '@/store/EventBus';
import { externalPolicyName } from '@/utilities/OrgConfig';
import { cacEntityName, externalAgencyName, externalPolicySourceIDs } from '@/utilities/Policies';
import { filters } from '@/utilities/Filters';
import AppButton from '@/components/v2/AppButton.vue';
import {
  Download,
  Minus,
  Plus,
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  FilePlus2,
  FileText,
  ExternalLink,
  Pencil,
  EllipsisVertical,
  Trash2,
  Building,
  Briefcase,
  LayoutList,
  PaperclipIcon,
  Calendar,
} from '@lucide/vue';
import { PolicySourceAOR } from '@/utilities/PolicySources';
import { chipColors } from '@/utilities/Color';
import ConsolidatedEditPolicyModal from '@/components/v2/policy/ConsolidatedEditPolicyModal.vue';
import DocumentModal from '@/components/v2/document/DocumentModal.vue';
import { USStatesMap, sortOptionForColumn } from '@/views/policy/PolicyDetails';
import { ContextMenuRoot, ContextMenuTrigger, ContextMenuContent, ContextMenuItem, ContextMenuSeparator, ContextMenuPortal } from 'reka-ui';
import AppModal from '@/components/v2/AppModal.vue';
import MenuDropdown from '@/components/v2/MenuDropdown.vue';
import PolicyAIStatusCell from '@/components/v2/PolicyAIStatusCell.vue';
import PolicyBinderAIStatusModal from '@/components/v2/PolicyBinderAIStatusModal.vue';

export default {
  name: 'PolicyDetailTable',
  components: {
    Plus,
    Minus,
    Calendar,
    AlertTriangle,
    AppButton,
    MenuDropdown,
    PolicySublimits,
    PolicyDeleteModal,
    ConsolidatedEditPolicyModal,
    FilePlus2,
    FileText,
    DocumentModal,
    Download,
    ContextMenuRoot,
    ContextMenuTrigger,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuSeparator,
    ContextMenuPortal,
    AppModal,
    ExternalLink,
    EllipsisVertical,
    PolicyAIStatusCell,
    PolicyBinderAIStatusModal,
  },
  props: {
    visibleFields: Array,
    perspective: {
      type: String,
      default: 'account',
    },
    policies: Array,
    policyHierarchy: Array,
    showChildren: Boolean,
    autoSort: {
      type: Boolean,
      default: true,
    },
    /**
     * Renders the consolidated 8-column layout (CAC-5074). Sorting is driven by the
     * caller's sort menu and the clickable headers, never by v-tablesort.
     */
    consolidated: {
      type: Boolean,
      default: false,
    },
    /**
     * Active consolidated sort as { value, direction }; renders the direction arrow on the
     * matching header, and header clicks emit `consolidated-sort` with the option value the
     * column drives. When absent, consolidated headers stay inert.
     */
    consolidatedSort: {
      type: Object,
      default: null,
    },
    sorts: Array,
    extraColumnTitle: { type: String, required: false, default: '' },
    policyDocumentTypes: Array,
    height: Number,
    showWithoutProgram: { type: Boolean, default: false },
    policySources: Array,
    /** Optional function returning extra menu items to show at the top of each row's dropdown (same style as built-in items). */
    getPrependMenuItems: { type: Function, default: undefined },
  },
  data() {
    return {
      filters,
      headerFixedY: 0,
      sublimitPolicy: undefined,
      sublimitDialogShow: false,
      ACCEPTED_FILE_DOCUMENT_TYPES,
      sublimits: [],
      subretentions: [],
      editPolicy: null,
      editPolicySourceId: 0,
      initialRecordKey: 'policy',
      initialEditTab: 'structure',
      showPolicyEditModal: false,
      PolicySourceMultiCarrierSchedules,
      usStatesLookupMap: USStatesMap,
      outsideClicked: false,
      deleteModalData: {
        policyNumber: '',
        accountId: 0,
        policyId: 0,
      },
      showDeleteModal: false,
      aiStatusModalOpen: false,
      aiStatusModalPolicyId: 0,
      aiStatusModalAccountId: 0,
    };
  },
  computed: {
    externalSourceIDs() {
      return externalPolicySourceIDs(this.policySources);
    },
    isCarrierPerspective() {
      return this.perspective === policyDetailsType.Carrier;
    },
    isWholesalerPerspective() {
      return this.perspective === policyDetailsType.Wholesaler;
    },
    hasExtraColumnSlotData() {
      return !!this.$slots.extraColumn;
    },
    hasExtraColumnHeader() {
      return !!this.$slots.extraColumnHeader;
    },
    isAClientAccount() {
      return IsUserAClient(Store.state.user);
    },
    isAMarketUser() {
      return IsMarketUser(Store.state.user);
    },
    isAnAdminOrBrokerUser() {
      return IsUserAnAdmin(Store.state.user) || IsUserABroker(Store.state.user);
    },
    canSeeAIStatus() {
      return this.isAnAdminOrBrokerUser;
    },
    hasPoliciesWithoutProgram() {
      return this.policies.some((policy) => policy.inProgram === false);
    },
    headerArrowIcon() {
      return this.consolidatedSort?.direction === 'ascending' ? ArrowUp : ArrowDown;
    },
    /**
     * The expand column only earns its width when something can actually be expanded —
     * otherwise the consolidated table leads with an empty column.
     */
    hasExpandablePolicies() {
      return (this.policyHierarchy ?? []).some((policy) => policy.hasChildren);
    },
    isOnAccountPolicyDetailPage() {
      return this.$route.name === 'account-policy-detail';
    },
    isOnAccountProgramDetailsPage() {
      return this.$route.name === 'account-program-details';
    },
    isOnParentAccountPolicyDetailPage() {
      return this.$route.name === 'parent-account-policy-detail';
    },
    isOnCarrierPolicyDetailPage() {
      return this.$route.name === 'carrier-policy-detail';
    },
  },
  methods: {
    displayStatutory(coverageType) {
      return coverageType === 'Work Comp';
    },
    openAIStatusModal(policyId, accountId) {
      this.aiStatusModalPolicyId = policyId;
      this.aiStatusModalAccountId = accountId ?? 0;
      this.aiStatusModalOpen = true;
    },
    externalPolicyName,
    className(key) {
      if (!this.autoSort) {
        const sort = this.sorts.find((s) => s.key === key);
        if (sort) {
          return { sorted: true, [sort.direction]: true };
        }
      }
      return null;
    },
    formatCurrency,
    compactMoney,
    calculateILF,
    calculatePPM,
    /** Whether the active consolidated sort is the one this column header drives. */
    isHeaderSortActive(column) {
      if (!this.consolidatedSort) return false;
      return sortOptionForColumn(column, this.consolidatedSort.value) === this.consolidatedSort.value;
    },
    onHeaderSort(column) {
      if (!this.consolidatedSort) return;
      const option = sortOptionForColumn(column, this.consolidatedSort.value);
      if (option) this.$emit('consolidated-sort', option);
    },
    /** "10/1/25 – 10/1/26" for the consolidated Term column. */
    formatTerm(policy) {
      const effective = filters.shorterdate(policy.effectiveDate);
      const expires = filters.shorterdate(policy.expirationDate);
      if (!effective && !expires) return '';
      return `${effective || '--'} – ${expires || '--'}`;
    },
    /**
     * Retention and attachment are mutually exclusive per policy — a primary layer has a
     * retention, an excess layer attaches above one — so the consolidated table shows
     * whichever applies in a single labelled cell.
     */
    retentionAttachment(policy) {
      const isExcess = policy.peFlag === 'Excess' || Number(policy.attachmentPoint) > 0;
      const value = isExcess ? policy.attachmentPoint : policy.retention;
      return {
        label: isExcess ? 'Attach' : 'Retention',
        value: value === null || value === undefined || value === '' ? null : compactMoney(value),
      };
    },
    /** Occurrence/aggregate for the consolidated Limits column; `null` renders as a muted dash. */
    limitValue(policy, field) {
      if (this.displayStatutory(policy.coverageType)) return 'Statutory';
      const value = policy[field];
      return value === null || value === undefined || value === '' ? null : compactMoney(value);
    },
    toggleExpand(index, policy) {
      const open = !policy.expanded;
      this.$emit('expand', open, policy);
    },
    cacEntityFor(policy) {
      return cacEntityName(policy, this.externalSourceIDs);
    },
    externalAgencyFor(policy) {
      return externalAgencyName(policy, this.externalSourceIDs);
    },
    isExternalPolicy(policy) {
      return this.policySources
        .filter((ps) => ps.IsExternal)
        .map((ps) => ps.ID)
        .includes(policy.policySourceID);
    },
    // The API refuses a delete on AllowPolicyDelete, not IsExternal. Every external source today
    // allows deletion, so the two only diverge for a source added later.
    canDeletePolicy(policy) {
      return Boolean(this.policySources.find((ps) => ps.ID === policy.policySourceID)?.AllowPolicyDelete);
    },
    // Sources with a label of their own, plus anything the PolicySource table marks
    // external -- so a new external source shows its chip without being listed here.
    hasChip(policy) {
      return (
        this.isExternalPolicy(policy) ||
        [PolicySourceAOR, PolicySourceControlling, PolicySourceParticipant, PolicySourceMultiCarrierSchedules].includes(
          policy.policySourceID,
        )
      );
    },
    getPolicyChipLabel(policy) {
      switch (policy.policySourceID) {
        case PolicySourceAOR:
          return 'BOR';
        case PolicySourceControlling:
          return 'Controlling';
        case PolicySourceParticipant:
          return 'Participant';
        case PolicySourceMultiCarrierSchedules:
          return 'Multi-Carrier';
        default:
          break;
      }

      return this.externalPolicyName();
    },
    getPolicyChipColor(policy) {
      switch (policy.policySourceID) {
        case PolicySourceControlling:
          return `${chipColors[1].bgColor} ${chipColors[1].textColor}`;
        case PolicySourceParticipant:
        case PolicySourceMultiCarrierSchedules:
          return `${chipColors[2].bgColor} ${chipColors[2].textColor}`;
        default:
          break;
      }

      return `${chipColors[0].bgColor} ${chipColors[0].textColor}`;
    },
    emitSort(e) {
      const { dataset } = e.target;
      const { sortkey } = dataset;
      if (sortkey) {
        this.$emit('sort', sortkey);
      }
    },
    trackAutoSortHeaderClick(e) {
      const td = e.target.nodeName.toLowerCase() === 'td' ? e.target : e.target.closest('td');
      if (!td || !td.dataset.sortkey || td.classList.contains('nosort') || td.classList.contains('actions')) {
        return;
      }
      // This listener sits on the header row, so it runs before the v-tablesort
      // directive's thead listener flips the sort classes — the upcoming direction
      // is derived here the same way the directive derives it.
      const direction = td.classList.contains('ascending') ? 'descending' : 'ascending';
      trackPolicyDetailsSort(td.dataset.sortkey, direction, 'standard');
    },
    handleScroll() {
      if (window.scrollY > this.headerFixedY) {
        this.$refs.tableHeader.style.transform = `translate3d(0, ${window.scrollY - this.headerFixedY}px, 100px)`;
      } else {
        this.$refs.tableHeader.style.transform = 'none';
      }
    },
    async showSublimits(policy) {
      const [sublimits, subretentions] = await Promise.all([policyService.sublimits(policy.uniqPolicy), policyService.subretentions(policy.uniqPolicy)]);
      this.sublimits = sublimits;
      this.subretentions = subretentions;
      this.sublimitPolicy = policy;
      this.sublimitDialogShow = true;
    },
    // The read-only view hands off to the consolidated edit modal, opened on the owning line's
    // sublimits tab, so there is one place where sublimits and subretentions are changed.
    editSublimitsInPolicyModal(policyLineId) {
      const policy = this.sublimitPolicy;
      this.closeSublimits();
      if (!policy) return;
      const anchor = this.policyHierarchy?.find((p) => p.uniqPolicy === policy.uniqPolicy && !p.isChildPolicy) || policy;
      this.editPolicy = anchor;
      this.editPolicySourceId = anchor?.policySourceID || 0;
      this.initialRecordKey = policyLineId ? `line:${policyLineId}` : 'policy';
      this.initialEditTab = policyLineId ? 'sublimits' : 'structure';
      this.showPolicyEditModal = true;
    },
    closeSublimits() {
      this.sublimitDialogShow = false;

      setTimeout(() => {
        this.sublimitPolicy = undefined;
        this.sublimits = [];
        this.subretentions = [];
      }, 200);
    },
    downloadPolicySublimits() {
      if (this.sublimits) {
        const filename = `policy-${this.sublimitPolicy.policyID}-sublimits-subretentions`;
        DownloadTablesToCSV(
          [
            { selector: '#policy-sublimits', title: 'Sublimits' },
            { selector: '#policy-subretentions', title: 'Subretentions' },
          ],
          filename,
        );
      }
    },
    handleEditPolicyClick(policy, isPolicyLine = false, parentPolicy = null) {
      // When invoked from a line row, the consolidated modal still needs the parent
      // policy as its anchor, with the line pre-selected in the side nav.
      let anchor = policy;
      let initialKey = 'policy';
      if (isPolicyLine) {
        anchor = parentPolicy || this.policyHierarchy?.find((p) => p.uniqPolicy === policy.uniqPolicy && !p.isChildPolicy);
        if (!anchor) {
          anchor = policy;
        }
        if (policy.uniqLine) {
          initialKey = `line:${policy.uniqLine}`;
        }
      }
      this.editPolicy = anchor;
      this.editPolicySourceId = anchor?.policySourceID || 0;
      this.initialRecordKey = initialKey;
      this.showPolicyEditModal = true;
    },
    handleDocumentUploadMenuClick(accountId, policyId, mainPolicyNumber) {
      this.$refs.uploadDocumentModalRef.open(accountId, policyId, mainPolicyNumber);
    },
    openDeleteModal(policy) {
      this.deleteModalData.policyNumber = policy.uniqPolicy;
      this.deleteModalData.accountId = Number(policy.accountID);
      this.deleteModalData.policyId = Number(policy.uniqPolicy);
      this.showDeleteModal = true;
    },
    getPolicyMenuItems(policy) {
      const items = [];
      if (!this.isAMarketUser && !this.isAClientAccount) {
        items.push({
          label: 'Edit Policy',
          handler: () => this.handleEditPolicyClick(policy),
          icon: Pencil,
        });
        items.push({
          label: 'Upload Document',
          handler: () => this.handleDocumentUploadMenuClick(Number(policy.accountID), Number(policy.uniqPolicy), policy.policyID),
          icon: PaperclipIcon,
          testId: 'attach-policy-document',
        });
      }
      if (
        this.isExternalPolicy(policy) &&
        this.canDeletePolicy(policy) &&
        !this.isCarrierPerspective &&
        !this.isWholesalerPerspective &&
        !this.isAClientAccount &&
        !this.isAMarketUser
      ) {
        items.push({
          label: 'Delete Policy',
          handler: () => this.openDeleteModal(policy),
          icon: Trash2,
          iconColorClass: 'text-error',
          testId: 'delete-policy',
        });
      }
      if (policy.accountID && !this.isOnAccountPolicyDetailPage && !this.isOnAccountProgramDetailsPage && !this.isAMarketUser) {
        items.push({
          label: `View ${policy.accountName}`,
          handler: () => this.$router.push(`/account/${policy.accountID}/policy-detail`),
          icon: Building,
        });
      }
      if (policy.parentAccountID && policy.accountID !== policy.parentAccountID && !this.isOnParentAccountPolicyDetailPage) {
        items.push({
          label: `View ${policy.parentAccountName}`,
          handler: () => this.$router.push(`/parent/account/${policy.parentAccountID}/policy-detail`),
          icon: Building,
        });
      }
      if (policy.carrierID && !this.isAClientAccount && !this.isOnCarrierPolicyDetailPage) {
        items.push({
          label: `View ${policy.carrierGroup}`,
          handler: () => this.$router.push(`/carrier/${policy.carrierID}/policy-detail`),
          icon: Briefcase,
        });
      }
      items.push({
        label: 'View Sublimits & Subretentions',
        handler: () => this.showSublimits(policy),
        icon: LayoutList,
        disabled: !policy.hasSublimits,
        testId: 'view-sublimits',
      });
      return items;
    },
    getChildPolicyMenuItems(childPolicy, parentPolicy) {
      if (!this.isAClientAccount) {
        return [
          {
            label: 'Edit Policy Line',
            handler: () => this.handleEditPolicyClick(childPolicy, true, parentPolicy),
            icon: Pencil,
          },
        ];
      }
      return [];
    },
    closeDeleteModal() {
      this.showDeleteModal = false;
    },
    emitRefreshKPIDataEvent() {
      dispatchEvent(new CustomEvent(Events.REFRESH_ACCOUNT_KPI));
    },
    handleClickOutsideContextMenu(event) {
      if (this.outsideClicked) {
        this.outsideClicked = false;
        event.stopPropagation();
        event.preventDefault();
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutsideContextMenu, true);
    if (!this.autoSort && !this.consolidated) {
      this.$refs.headerRow.addEventListener('click', this.emitSort);
    } else if (this.autoSort && !this.consolidated) {
      this.$refs.headerRow.addEventListener('click', this.trackAutoSortHeaderClick);
    }
    const rect = this.$refs.tableHeader.getBoundingClientRect();
    const appBarElement = document.querySelector('header[data-header]');
    if (appBarElement) {
      this.headerFixedY = rect.top + window.scrollY - appBarElement.getBoundingClientRect().height;
    }
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutsideContextMenu);
    if (!this.autoSort && !this.consolidated) {
      this.$refs.headerRow.removeEventListener('click', this.emitSort);
    } else if (this.autoSort && !this.consolidated) {
      this.$refs.headerRow.removeEventListener('click', this.trackAutoSortHeaderClick);
    }
    window.removeEventListener('scroll', this.handleScroll);
  },
  updated() {
    this.$refs.policyDetailTableRef.style.height = this.height && `${this.height}px`;
  },
};
</script>

<style scoped>
/*noinspection CssUnusedSymbol*/
.external-policy {
  background: var(--v-offwhite-base);
}

.layer-id > div {
  word-wrap: break-word;
  display: inline-block;
}

.child {
  background-color: #f7f7f7;
  font-size: 0.8em;
}

.last-child-row > td {
  border-bottom: 1px solid var(--v-gray-lighten3);
}

.url-text {
  color: inherit;
  display: block;
}

.url-icon {
  visibility: hidden;
  padding-left: 0.25em;
}

@media (pointer: none), (pointer: coarse) {
  .url-icon {
    visibility: visible;
  }
}

tr:hover .url-icon {
  visibility: visible;
}

table {
  transform-style: preserve-3d;
}

thead {
  transform: translate3d(0, 0, 100px);
  position: sticky;
  top: 0;
  z-index: 2;
}

tbody {
  transform: translate3d(0, 0, 0);
}
</style>
